import { Component, type ErrorInfo, type ReactNode } from 'react'
import { captureException } from './helpers'
import { ErrorFallback } from './ErrorFallback'
import StaleBundleNotice from '@/components/common/StaleBundleNotice'
import { isStaleBundleRecoverable } from '@/lib/stale-bundle'

interface SentryErrorBoundaryProps {
  children: ReactNode
}

interface SentryErrorBoundaryState {
  error: Error | null
}

export class SentryErrorBoundary extends Component<
  SentryErrorBoundaryProps,
  SentryErrorBoundaryState
> {
  state: SentryErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown): SentryErrorBoundaryState {
    return {
      error: error instanceof Error ? error : new Error(String(error)),
    }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    captureException(error, {
      componentStack: info.componentStack ?? undefined,
      mechanism: 'react-error-boundary',
    })
  }

  resetError = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    if (this.state.error) {
      if (isStaleBundleRecoverable(this.state.error)) {
        return <StaleBundleNotice />
      }
      return (
        <ErrorFallback error={this.state.error} resetError={this.resetError} />
      )
    }
    return this.props.children
  }
}
